import { inject } from '@angular/core';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { FieldDefinition, FieldBuilder, MenuCategories, MenuSubcategories, MENU_CATEGORY_KEY, MENU_SUBCATEGORY_KEY, MENU_NAME_SUFFIX, DEFAULT_MAX_RESULT_COUNT, ZnSelectQueryParams, convertDisplayDateToIsoDateOnly } from '@zendra/shared';
import { ForestryBaseService } from '../proxy/geographic/controllers';

FieldBuilder.setNamespace('Geographic');

export const FORESTRYBASE_FIELDS: FieldDefinition[] = [
  FieldBuilder
    .text('regionCode', 'Entity.ForestryBase.RegionCode')
    .maxLength(10)
    .build(),
  FieldBuilder
    .text('regionName', 'Entity.ForestryBase.RegionName')
    .maxLength(100)
    .build(),
  FieldBuilder
    .text('zoneCode', 'Entity.ForestryBase.ZoneCode')
    .maxLength(10)
    .build(),
  FieldBuilder
    .text('zoneName', 'Entity.ForestryBase.ZoneName')
    .maxLength(100)
    .build(),
  FieldBuilder
    .text('propertyCode', 'Entity.ForestryBase.PropertyCode')
    .maxLength(4)
    .build(),
  FieldBuilder
    .text('propertyName', 'Entity.ForestryBase.PropertyName')
    .maxLength(100)
    .build(),
  FieldBuilder
    .text('standName', 'Entity.ForestryBase.StandName')
    .maxLength(100)
    .build(),
  FieldBuilder
    .number('standCycle', 'Entity.ForestryBase.StandCycle')
    .build(),
  FieldBuilder
    .number('standRotation', 'Entity.ForestryBase.StandRotation')
    .build(),
  FieldBuilder
    .number('standArea', 'Entity.ForestryBase.StandArea')
    .decimal()
    .build(),
  FieldBuilder
    .text('periodName', 'Entity.ForestryBase.PeriodName')
    .maxLength(100)
    .build(),
  FieldBuilder
    .text('species', 'Entity.ForestryBase.Species')
    .maxLength(100)
    .build(),
  FieldBuilder
    .text('geneticMaterialType', 'Entity.ForestryBase.GeneticMaterialType')
    .maxLength(50)
    .build(),
  FieldBuilder
    .text('geneticMaterial', 'Entity.ForestryBase.GeneticMaterial')
    .maxLength(200)
    .build(),
  FieldBuilder
    .date('plantationDate', 'Entity.ForestryBase.PlantationDate')
    .build(),
  FieldBuilder
    .checkbox('isOwnPlantation', 'Entity.ForestryBase.IsOwnPlantation')
    .build(),
  FieldBuilder
    .number('age', 'Entity.ForestryBase.Age')
    .decimal()
    .build(),
  FieldBuilder
    .number('volumePerHectare', 'Entity.ForestryBase.VolumePerHectare')
    .decimal()
    .build(),
  FieldBuilder
    .text('propertyTypeName', 'Entity.ForestryBase.PropertyTypeName')
    .maxLength(40)
    .build(),
  FieldBuilder
    .text('propertyTypeGroupName', 'Entity.ForestryBase.PropertyTypeGroupName')
    .maxLength(40)
    .build(),
  FieldBuilder
    .text('rotationUse', 'Entity.ForestryBase.RotationUse')
    .maxLength(100)
    .build(),
  FieldBuilder
    .text('forestrySituation', 'Entity.ForestryBase.ForestrySituation')
    .maxLength(100)
    .build(),
  FieldBuilder
    .text('forestryStatus', 'Entity.ForestryBase.ForestryStatus')
    .maxLength(100)
    .build(),
  FieldBuilder
    .number('eucalyptusArea', 'Entity.ForestryBase.EucalyptusArea')
    .decimal()
    .build(),
  FieldBuilder
    .text('departmentName', 'Entity.ForestryBase.DepartmentName')
    .maxLength(100)
    .build(),
  FieldBuilder
    .number('regionId', 'Entity.ForestryBase.RegionId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .number('zoneId', 'Entity.ForestryBase.ZoneId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .number('propertyId', 'Entity.ForestryBase.PropertyId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .number('locationId', 'Entity.ForestryBase.LocationId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .number('standId', 'Entity.ForestryBase.StandId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .number('periodId', 'Entity.ForestryBase.PeriodId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .number('speciesId', 'Entity.ForestryBase.SpeciesId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .number('geneticMaterialTypeId', 'Entity.ForestryBase.GeneticMaterialTypeId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .number('geneticMaterialId', 'Entity.ForestryBase.GeneticMaterialId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .number('propertyTypeId', 'Entity.ForestryBase.PropertyTypeId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .number('propertyTypeGroupId', 'Entity.ForestryBase.PropertyTypeGroupId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .number('rotationUseId', 'Entity.ForestryBase.RotationUseId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .number('forestrySituationId', 'Entity.ForestryBase.ForestrySituationId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .number('forestryStatusId', 'Entity.ForestryBase.ForestryStatusId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .number('departmentId', 'Entity.ForestryBase.DepartmentId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .checkbox('isPefc', 'Entity.ForestryBase.IsPefc')
    .build(),
  FieldBuilder
    .checkbox('isFsc', 'Entity.ForestryBase.IsFsc')
    .build(),
  FieldBuilder
    .number('landContractId', 'Entity.ForestryBase.LandContractId')
    .hideByDefault()
    .build(),
  FieldBuilder
    .text('landContract', 'Entity.ForestryBase.LandContract')
    .maxLength(20)
    .build(),
  FieldBuilder
    .number('plantedArea', 'Entity.ForestryBase.PlantedArea')
    .decimal()
    .build(),
  FieldBuilder
    .checkbox('isManagedByFO', 'Entity.ForestryBase.IsManagedByFO')
    .build(),
  FieldBuilder
    .dropdown('reportDate', 'Entity.ForestryBase.ReportDate')
    .optionsLoader((params?: ZnSelectQueryParams) => {
      const search = params?.search?.trim() ?? '';
      const resolveSelectedDate = !search && params?.preferCache && params.ids?.length;
      const dateText = resolveSelectedDate
        ? params.ids![0].substring(0, 10).split('-').reverse().join('/')
        : search;
      const searchDate = dateText ? convertDisplayDateToIsoDateOnly(dateText) : null;
      if (dateText && !searchDate) {
        return of({ items: [], totalCount: 0 });
      }

      const service = inject(ForestryBaseService);
      return service
        .getAvailableReportDates({
          ...(searchDate ? { searchDate } : {}),
          skipCount: params?.skipCount ?? 0,
          maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
        })
        .pipe(
          map(result => ({
            totalCount: result.totalCount,
            items: (result.items ?? []).flatMap(item => {
              if (!item.date) {
                return [];
              }

              return [{
                key: item.date,
                value: item.date.substring(0, 10).split('-').reverse().join('/'),
              }];
            }),
          }))
        );
    })
    .formatter((_row, _cell, value) => {
      if (!value || typeof value !== 'string') {
        return '';
      }
      return value.substring(0, 10).split('-').reverse().join('/');
    })
    .prefilter({ queryKey: 'reportDate', autoSelectFirst: true })
    .build()
];

export const FORESTRYBASE_CONFIG = {
  entityName: 'ForestryBase',
  localizationKey: 'Geographic::Entity.ForestryBase',
  fields: FORESTRYBASE_FIELDS,
  permission: 'PlanningGeography.ForestryBases',
  showInMenu: true,
  menuCategory: MENU_CATEGORY_KEY + MenuCategories.Planning + MENU_NAME_SUFFIX,
  menuSubcategory: MENU_SUBCATEGORY_KEY + MenuSubcategories.TablesPlanning + MENU_NAME_SUFFIX,
};
